import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import useExchangeToken from '../../hooks/useExchangeToken';
import { apiUrl } from '../../constants';

// This is a client-side readiness check only. The backend verifies the signature.
export function isBackendAccessToken(token) {
  try {
    const claims = jwtDecode(token);
    return claims.token_type === 'access' && Number(claims.exp) > Date.now() / 1000 + 60;
  } catch { return false; }
}

const endpoints = Object.entries(apiUrl)
  .filter(([key]) => key.startsWith('TUMBLER_'))
  .map(([, url]) => new URL(url));

export function isTumblerRequest(config) {
  try {
    const url = new URL(config.url, config.baseURL || window.location.origin);
    return endpoints.some((endpoint) => url.origin === endpoint.origin && url.pathname.startsWith(endpoint.pathname));
  } catch { return false; }
}

export default function TumblerAuthBoundary({ children }) {
  const auth = useSelector((state) => state.auth);
  const exchangeToken = useExchangeToken();
  const current = useRef({ auth, exchangeToken });
  current.current = { auth, exchangeToken };
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ ready: false, error: false });

  useEffect(() => {
    let active = true;
    let exchangeFlight;
    const getToken = async (force = false) => {
      const { auth: latest, exchangeToken: exchange } = current.current;
      if (!force && isBackendAccessToken(latest.access_token)) return latest.access_token;
      if (!exchangeFlight) {
        exchangeFlight = exchange({ force: true }).then((token) => {
          if (!isBackendAccessToken(token)) throw new Error('Unable to obtain a backend session. Please sign in again.');
          return token;
        }).finally(() => { exchangeFlight = null; });
      }
      return exchangeFlight;
    };
    const requestId = axios.interceptors.request.use(async (config) => {
      if (!isTumblerRequest(config) || config._tumblerAuthRetried) return config;
      const token = await getToken();
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      return config;
    });
    const responseId = axios.interceptors.response.use((response) => response, async (error) => {
      const config = error.config;
      if (!active || !config || !isTumblerRequest(config) || error.response?.status !== 401 || config._tumblerAuthRetried) throw error;
      config._tumblerAuthRetried = true;
      const token = await getToken(true);
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      return axios.request(config);
    });
    setState({ ready: false, error: false });
    getToken().then(() => {
      if (active) setState({ ready: true, error: false });
    }).catch(() => {
      if (active) setState({ ready: false, error: true });
    });
    return () => {
      active = false;
      axios.interceptors.request.eject(requestId);
      axios.interceptors.response.eject(responseId);
    };
  }, [auth.accessToken, attempt]);

  if (state.error) return <Alert severity="error" action={<Button onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}>Unable to authenticate with MES. Retry or sign in again.</Alert>;
  if (!state.ready) return <Box sx={{ p: 4 }}><CircularProgress aria-label="Preparing MES session" /></Box>;
  return children;
}
